import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  MedicalDocument,
  MedicalTimeline,
  TimelineEvent,
  TimelineEventCategory,
} from '../../api/documents';
import type { MemberTabId } from './MemberTabs';

interface MemberActivityFeedProps {
  timeline: MedicalTimeline | null;
  documents: MedicalDocument[];
  onSwitchTab: (tab: MemberTabId) => void;
}

type FeedItem =
  | {
      kind: 'event';
      id: string;
      iso: string;
      title: string;
      summary: string;
      category: TimelineEventCategory;
      sourceDocId: number | null;
    }
  | {
      kind: 'doc';
      id: number;
      iso: string;
      title: string;
      summary: string;
      docTypes: string[];
    };

const CATEGORY_LABEL: Record<TimelineEventCategory, string> = {
  lab: 'Laborator',
  imaging: 'Imagistică',
  clinical: 'Clinic',
  procedure: 'Procedură',
  medication: 'Medicație',
  diagnosis: 'Diagnostic',
  vaccination: 'Vaccinare',
  consultation: 'Consultație',
  other: 'Alte',
};

const RO_MONTHS_SHORT = ['ian.', 'feb.', 'mar.', 'apr.', 'mai', 'iun.', 'iul.', 'aug.', 'sep.', 'oct.', 'noi.', 'dec.'];

function parseDate(iso: string): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const EmptyIcon = () => (
  <svg className="ov-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const MemberActivityFeed: React.FC<MemberActivityFeedProps> = ({
  timeline,
  documents,
  onSwitchTab,
}) => {
  const navigate = useNavigate();

  const items = useMemo<FeedItem[]>(() => {
    const out: FeedItem[] = [];

    // last 5 timeline events (events list is ascending chronological → take the tail).
    const events = (timeline?.events ?? []) as TimelineEvent[];
    const recentEvents = events.slice(-5).reverse();
    for (const ev of recentEvents) {
      out.push({
        kind: 'event',
        id: ev.id,
        iso: ev.date,
        title: ev.title,
        summary: ev.summary,
        category: ev.category,
        sourceDocId: ev.source_document_ids[0] ?? null,
      });
    }

    // last 3 uploaded documents (sorted desc by upload_date).
    const sortedDocs = [...documents]
      .filter((d) => Boolean(d.upload_date))
      .sort((a, b) => {
        const ta = new Date(a.upload_date).getTime();
        const tb = new Date(b.upload_date).getTime();
        return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
      })
      .slice(0, 3);
    for (const d of sortedDocs) {
      out.push({
        kind: 'doc',
        id: d.id,
        iso: d.upload_date,
        title: d.original_filename || `Document #${d.id}`,
        summary: d.notes || '',
        docTypes: d.document_types ?? [],
      });
    }

    out.sort((a, b) => {
      const ta = new Date(a.iso).getTime();
      const tb = new Date(b.iso).getTime();
      return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
    });

    return out.slice(0, 8);
  }, [timeline, documents]);

  if (items.length === 0) {
    return (
      <div className="ov-empty" role="status">
        <EmptyIcon />
        <div className="ov-empty-title">Niciun eveniment recent</div>
        <div className="ov-empty-text">
          Încarcă un document medical sau generează cronologia pentru a vedea activitatea.
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => onSwitchTab('docs')}
        >
          Vezi documentele
        </button>
      </div>
    );
  }

  const handleOpen = (item: FeedItem) => {
    if (item.kind === 'doc') {
      navigate(`/documents/${item.id}`);
      return;
    }
    if (item.sourceDocId != null) {
      navigate(`/documents/${item.sourceDocId}`);
      return;
    }
    onSwitchTab('timeline');
  };

  return (
    <ul className="ov-feed">
      {items.map((item) => {
        const d = parseDate(item.iso);
        const day = d ? d.getDate() : '—';
        const month = d ? RO_MONTHS_SHORT[d.getMonth()] : '';
        const year = d ? d.getFullYear() : '';
        const key = item.kind === 'event' ? `e-${item.id}` : `d-${item.id}`;
        return (
          <li key={key}>
            <button
              type="button"
              className="ov-feed-item"
              onClick={() => handleOpen(item)}
            >
              <span className="ov-feed-date" aria-hidden>
                <span className="ov-feed-date-day">{day}</span>
                <span className="ov-feed-date-month">{month}</span>
                <span className="ov-feed-date-year">{year}</span>
              </span>
              <span className="ov-feed-body">
                <span className="ov-feed-title">{item.title}</span>
                {item.summary && <span className="ov-feed-summary">{item.summary}</span>}
                <span className="ov-feed-badges">
                  {item.kind === 'event' ? (
                    <span className="ov-feed-badge">{CATEGORY_LABEL[item.category]}</span>
                  ) : (
                    <>
                      <span className="ov-feed-badge ov-feed-badge-doc">Document încărcat</span>
                      {item.docTypes.map((t) => (
                        <span key={`type-${t}`} className="ov-feed-badge">
                          {t}
                        </span>
                      ))}
                    </>
                  )}
                </span>
              </span>
              <span className="ov-feed-chevron" aria-hidden>
                <ChevronRight />
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export default MemberActivityFeed;
